import { Box, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";

import Loading from "../Loading";
import AiPersonalityForm, { type IFormValues } from "./AiPersonalityForm";
import {
    type AiPersonalityEditMutation,
    type AiPersonalityEditMutationVariables,
    type AiPersonalityQuery,
    type AiPersonalityQueryVariables,
    aiPersonalityQuery,
    editMutation,
} from "./queries";

function Edit(): JSX.Element {
    const { aiPersonalityId } = useParams();
    const navigate = useNavigate();
    const [editResult, executeEdit] = useMutation<AiPersonalityEditMutation, AiPersonalityEditMutationVariables>(
        editMutation,
    );
    const [result] = useQuery<AiPersonalityQuery, AiPersonalityQueryVariables>({
        query: aiPersonalityQuery,
        variables: {
            aiPersonalityId: Number.parseInt(aiPersonalityId!, 10),
        },
    });

    const handleSubmit = async (values: IFormValues) => {
        const result = await executeEdit({
            data: {
                id: Number.parseInt(aiPersonalityId!, 10),
                name: values.name,
                externalName: values.externalName || null,
                systemPrompt: values.systemPrompt,
                temperature: values.temperature,
                includeRetrieval: values.includeRetrieval,
                includeBrainstorm: values.includeBrainstorm,
                includeMissingInformation: values.includeMissingInformation,
                epsilon: values.epsilon,
                tags: values.tags,
                languages: values.languages,
                model: values.model,
                useCache: values.useCache,
                showInChat: values.showInChat,
                reasoningEffort: values.reasoningEffort,
                verbosity: values.verbosity,
                responseFormat: values.useResponseFormat && !values.includeRetrieval ? values.responseFormat : null,
                maxIterations: values.maxIterations,
            },
        });
        if (result.error) {
            notifications.show({
                title: "Failed to save AI personality",
                message: result.error.message,
            });
        } else {
            const aiPersonality = result.data!.aiPersonalityEdit;
            notifications.show({
                title: "AI personality saved",
                message: `AI personality ${aiPersonality.name} updated`,
            });
            navigate("..");
        }
    };

    let main = null;
    if (result.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else {
        const aiPersonality = result.data?.aiPersonality;
        if (!aiPersonality) {
            main = <Box>AI personality not found</Box>;
        } else {
            const initialValues = {
                name: aiPersonality.name,
                externalName: aiPersonality.externalName,
                systemPrompt: aiPersonality.systemPrompt,
                temperature: aiPersonality.temperature,
                includeRetrieval: aiPersonality.includeRetrieval,
                includeBrainstorm: aiPersonality.includeBrainstorm,
                includeMissingInformation: aiPersonality.includeMissingInformation,
                epsilon: aiPersonality.epsilon,
                tags: aiPersonality.tags,
                languages: aiPersonality.languages,
                model: aiPersonality.model,
                useCache: aiPersonality.useCache,
                showInChat: aiPersonality.showInChat || false,
                reasoningEffort: aiPersonality.reasoningEffort,
                useResponseFormat: !!aiPersonality.responseFormat,
                responseFormat: aiPersonality.responseFormat,
                verbosity: aiPersonality.verbosity,
                maxIterations: aiPersonality.maxIterations,
            };
            main = (
                <AiPersonalityForm
                    handleSave={handleSubmit}
                    initialValues={initialValues}
                    fetching={editResult.fetching}
                />
            );
        }
    }

    return (
        <>
            <Title order={2}>Edit AI personlity</Title>
            {main}
            <Link to="..">Back</Link>
        </>
    );
}

export default Edit;
